import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getPublicReport, exportPublicWord, exportPublicPdf, logPublicSourceView } from 'services/reportingService'
import ReportAnswerCard from 'features/bpDetails/Components/reporting/ReportAnswerCard'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'
import { Skeleton, Collapse, IconButton, Tooltip, CircularProgress } from '@mui/material'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DescriptionIcon from '@mui/icons-material/Description'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'

const InsuranceGuruPublicReportPage = () => {
  const { token } = useParams()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedCategories, setExpandedCategories] = useState({})
  const [downloadingWord, setDownloadingWord] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [currentFile, setCurrentFile] = useState(null)

  // When a public viewer clicks a source citation, log the view (mirroring the
  // per-file audit pattern used by ExternalAccess file shares) and open it in
  // the same PdfFullScreenViewer component used by the authenticated report
  // view. We use the fresh pre-signed URL returned by the log endpoint — the
  // URL rendered at page load expires after about an hour.
  const handleSourceClick = async (source) => {
    if (!source?.id) {
      if (source?.fileUrl) {
        window.open(source.fileUrl, '_blank', 'noopener,noreferrer')
      }
      return
    }
    try {
      const res = await logPublicSourceView(token, source.id)
      const fresh = res?.data?.data || {}
      setCurrentFile({
        id: source.id,
        displayName: fresh.name || source.name,
        url: fresh.url || source.fileUrl,
        originalUrl: fresh.originalUrl || source.originalUrl,
        contentType: (fresh.name || source.name || '').toLowerCase().endsWith('.pdf')
          ? 'application/pdf'
          : null,
      })
      setPdfViewerOpen(true)
    } catch (_) {
      // Log failed — fall back to opening the cached pre-signed URL in a new tab
      if (source.fileUrl) {
        window.open(source.fileUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Unable to open this source.')
      }
    }
  }

  const isCpse = String(report?.reportType?.slug || '').startsWith('cpse')

  const triggerBlobDownload = (data, filename, mimeType) => {
    const blob = new Blob([data], { type: mimeType })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleDownloadWord = async () => {
    if (downloadingWord) return
    setDownloadingWord(true)
    try {
      const result = await exportPublicWord(token)
      const bpName = report?.buildingPassport?.name || 'report'
      const slug = (report?.reportType?.slug || 'report').toLowerCase()
      triggerBlobDownload(
        result.data,
        `${slug}-${bpName.replace(/\s+/g, '-')}.docx`,
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      )
    } catch (err) {
      toast.error('Failed to download Word document')
    } finally {
      setDownloadingWord(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (downloadingPdf) return
    setDownloadingPdf(true)
    try {
      const result = await exportPublicPdf(token)
      const bpName = report?.buildingPassport?.name || 'report'
      const slug = (report?.reportType?.slug || 'report').toLowerCase()
      triggerBlobDownload(
        result.data,
        `${slug}-${bpName.replace(/\s+/g, '-')}.pdf`,
        'application/pdf'
      )
    } catch (err) {
      toast.error('Failed to download PDF')
    } finally {
      setDownloadingPdf(false)
    }
  }

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await getPublicReport(token)
        setReport(response.data.data)
      } catch (err) {
        console.error('Failed to fetch public report:', err)
        setError(err.response?.data?.message || 'Report not found or link has expired.')
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      fetchReport()
    }
  }, [token])

  // Initialize all categories as expanded when report loads
  useEffect(() => {
    if (report?.answersByCategory && Object.keys(report.answersByCategory).length > 0) {
      const initialExpanded = {}
      Object.keys(report.answersByCategory).forEach(category => {
        initialExpanded[category] = true
      })
      setExpandedCategories(initialExpanded)
    }
  }, [report])

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  // Loading state
  if (loading) {
    return (
      <PageWrapper>
        <ContentContainer>
          <LoadingContainer>
            <LoadingHeader>
              <SkeletonIcon />
              <LoadingHeaderContent>
                <Skeleton variant="text" width="60%" height={36} />
                <Skeleton variant="text" width="40%" height={24} />
              </LoadingHeaderContent>
            </LoadingHeader>
            <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 2, mb: 3 }} />
            <LoadingCards>
              <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
            </LoadingCards>
          </LoadingContainer>
        </ContentContainer>
      </PageWrapper>
    )
  }

  // Error state
  if (error) {
    return (
      <PageWrapper>
        <ContentContainer>
          <ErrorContainer>
            <ErrorCard>
              <ErrorIconWrapper>
                <ErrorOutlineIcon sx={{ fontSize: 48, color: 'white' }} />
              </ErrorIconWrapper>
              <ErrorTitle>Report Not Available</ErrorTitle>
              <ErrorMessage>{error}</ErrorMessage>
            </ErrorCard>
          </ErrorContainer>
        </ContentContainer>
      </PageWrapper>
    )
  }

  // Report view
  return (
    <PageWrapper>
      <ContentContainer>
        <ReportHeader>
          <HeaderIcon>
            <AssessmentIcon />
          </HeaderIcon>
          <HeaderContent>
            <HeaderLabel>{report?.reportType?.name || 'Report'}</HeaderLabel>
            <HeaderTitle>{report?.buildingPassport?.name || 'Building Report'}</HeaderTitle>
            <HeaderMeta>
              {report?.buildingPassport?.street && (
                <MetaItem>
                  {[
                    report.buildingPassport.street,
                    report.buildingPassport.city,
                    report.buildingPassport.postalCode
                  ].filter(Boolean).join(', ')}
                </MetaItem>
              )}
              {report?.completedAt && (
                <MetaItem>
                  Generated: {new Date(report.completedAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </MetaItem>
              )}
              {report?.version && (
                <VersionBadge>Version {report.version}</VersionBadge>
              )}
            </HeaderMeta>
          </HeaderContent>
          <HeaderActions>
            <DownloadButton onClick={handleDownloadWord} disabled={downloadingWord} title="Download as Word">
              {downloadingWord ? <CircularProgress size={16} /> : <DescriptionIcon style={{ fontSize: 18 }} />}
              <span>Word</span>
            </DownloadButton>
            {!isCpse && (
              <DownloadButton onClick={handleDownloadPdf} disabled={downloadingPdf} title="Download as PDF">
                {downloadingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon style={{ fontSize: 18 }} />}
                <span>PDF</span>
              </DownloadButton>
            )}
          </HeaderActions>
        </ReportHeader>

        <AnswersContainer>
          {report?.answersByCategory && Object.entries(report.answersByCategory).map(([category, categoryData]) => (
            <CategorySection key={category}>
              <CategoryHeader onClick={() => toggleCategory(category)}>
                <CategoryTitleWrapper>
                  <CategoryBadge>{categoryData.answers?.length || 0}</CategoryBadge>
                  <CategoryTitle>{categoryData.label}</CategoryTitle>
                </CategoryTitleWrapper>
                <Tooltip title={expandedCategories[category] ? 'Collapse section' : 'Expand section'}>
                  <ExpandIconButton size="small">
                    {expandedCategories[category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ExpandIconButton>
                </Tooltip>
              </CategoryHeader>
              <Collapse in={expandedCategories[category]} timeout="auto">
                <AnswersList>
                  {categoryData.answers?.map((answer, index) => (
                    <ReportAnswerCard
                      key={answer.questionId || index}
                      answer={answer}
                      showSources={true}
                      canEdit={false}
                      onSourceClick={handleSourceClick}
                    />
                  ))}
                </AnswersList>
              </Collapse>
            </CategorySection>
          ))}
        </AnswersContainer>

        <Footer>
          <FooterText>
            This report was shared by Building Passport
          </FooterText>
        </Footer>
      </ContentContainer>

      <PdfFullScreenViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        file={currentFile}
        skipAccessLog
      />
    </PageWrapper>
  )
}

export default InsuranceGuruPublicReportPage

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100%;
  box-sizing: border-box;
  animation: ${fadeIn} 0.3s ease-out;
`

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
`

// Loading State Styles
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
`

const LoadingHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`

const SkeletonIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 50%, #e0e0e0 100%);
  background-size: 200% 200%;
  animation: shimmer 1.5s infinite;
  border-radius: 16px;
  flex-shrink: 0;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`

const LoadingHeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

const LoadingCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

// Error State Styles
const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 24px;
`

const ErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  padding: 48px 32px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`

const ErrorIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%);
  border-radius: 50%;
  margin-bottom: 24px;
`

const ErrorTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 12px 0;
`

const ErrorMessage = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #6B7280;
  margin: 0;
`

// Report Header Styles
const ReportHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 20px;
  }
`

const HeaderIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #D24724 0%, #B83D1F 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(210, 71, 36, 0.25);
  flex-shrink: 0;

  svg {
    font-size: 32px;
  }

  @media (max-width: 600px) {
    width: 56px;
    height: 56px;
    border-radius: 14px;

    svg {
      font-size: 28px;
    }
  }
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const HeaderLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #9CA3AF;
`

const HeaderTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1F2937;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`

const HeaderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 4px;

  @media (max-width: 600px) {
    justify-content: center;
  }
`

const MetaItem = styled.span`
  font-size: 14px;
  color: #6B7280;
`

const VersionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #33becc20;
  color: #33becc;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-self: center;

  @media (max-width: 600px) {
    align-self: stretch;
    justify-content: center;
  }
`

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: white;
  color: ${({ theme }) => theme.colors?.haiti || '#1F2937'};
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: #D24724;
    color: #D24724;
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

// Categories and Answers Styles
const AnswersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const CategorySection = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 600px) {
    border-radius: 12px;
  }
`

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  cursor: pointer;
  background: linear-gradient(to right, rgba(210, 71, 36, 0.03), transparent);
  border-bottom: 1px solid #E5E7EB;
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(to right, rgba(210, 71, 36, 0.07), rgba(210, 71, 36, 0.02));
  }

  @media (max-width: 600px) {
    padding: 16px 20px;
  }
`

const CategoryTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  background: #D24724;
  color: white;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
`

const CategoryTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1F2937;
  margin: 0;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`

const ExpandIconButton = styled(IconButton)`
  && {
    color: #9CA3AF;

    &:hover {
      color: #D24724;
      background: rgba(210, 71, 36, 0.06);
    }
  }
`

const AnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: #E5E7EB;
`

// Footer Styles
const Footer = styled.div`
  margin-top: 40px;
  padding: 24px;
  text-align: center;
  border-top: 1px solid #E5E7EB;
`

const FooterText = styled.p`
  font-size: 14px;
  color: #9CA3AF;
  margin: 0;
`
